import React, { useCallback, useEffect, useRef } from 'react';
import { IonContent, IonHeader, IonPage } from '@ionic/react';
import { useKeyboardAwareScroll } from '@flexo/hooks';
import { Capacitor } from '@capacitor/core';
import styles from './keyboard-aware-layout.module.scss';

export interface KeyboardAwareLayoutProps {
  /**
   * Content for the fixed header section
   */
  header?: React.ReactNode;

  /**
   * Content for the scrollable main section
   */
  children: React.ReactNode;

  /**
   * Content for the fixed footer section
   */
  footer?: React.ReactNode;

  /**
   * Extra space (in pixels) to add above the keyboard when scrolling an input into view
   * @default 8
   */
  extraPadding?: number;

  /**
   * Custom class name for the IonContent component
   */
  contentClassName?: string;

  /**
   * Additional props to pass to the IonContent component
   */
  contentProps?: Record<string, any>;
}

/**
 * A layout component that handles keyboard appearance and automatically scrolls
 * input fields into view when they are focused and the keyboard is visible.
 *
 * This component creates a 3-section layout:
 * 1. A fixed header at the top
 * 2. A scrollable content area in the middle that adjusts to available space
 * 3. A fixed position footer at the bottom that moves up when the keyboard appears
 *
 * Features:
 * - Footer animates smoothly when keyboard appears/disappears
 * - Focused inputs are automatically scrolled into view above the keyboard
 * - Tapping outside inputs dismisses the keyboard
 * - No auto-focus on mount - user interaction triggers focus
 * - Works on iOS & Android, in portrait & landscape orientations
 *
 * @example
 * ```tsx
 * <KeyboardAwareLayout
 *   header={<PageHeader title="Form Title" />}
 *   footer={<Button onClick={handleSubmit}>Submit</Button>}
 * >
 *   <div>
 *     <Textfield ref={emailRef} label="Email" />
 *     <Textfield ref={passwordRef} label="Password" type="password" />
 *   </div>
 * </KeyboardAwareLayout>
 * ```
 */
export const KeyboardAwareLayout: React.FC<KeyboardAwareLayoutProps> = ({
  header,
  children,
  footer,
  extraPadding = 8,
  contentClassName = '',
  contentProps = {},
}) => {
  // Use our custom hook to handle keyboard interactions
  const {
    contentRef,
    keyboardHeight,
    keyboardVisible,
    registerInput,
    hideKeyboard,
  } = useKeyboardAwareScroll({
    scrollPadding: extraPadding,
  });

  // Get platform information for device-specific handling
  const platform = Capacitor.getPlatform();
  const isAndroid = platform === 'android';

  // Reference to track tap event handling to prevent duplicates
  const isTapInProgress = useRef<boolean>(false);

  // Handler to dismiss keyboard when tapping on background
  const handleBackgroundTap = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      // Prevent duplicate handling
      if (isTapInProgress.current) return;

      // Only dismiss if we're clicking directly on the content or a container div
      // not on an input, button, or other interactive element
      const target = e.target as HTMLElement;
      const isInteractiveElement =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.tagName === 'BUTTON' ||
        target.closest('input') ||
        target.closest('textarea') ||
        target.closest('select') ||
        target.closest('button') ||
        target.closest('[role="button"]');

      if (!isInteractiveElement && keyboardVisible) {
        // Mark that we're processing a tap event
        isTapInProgress.current = true;

        // Hide the keyboard
        hideKeyboard();

        // Prevent event propagation
        e.stopPropagation();

        // Reset the flag after a delay
        setTimeout(() => {
          isTapInProgress.current = false;
        }, 300);
      }
    },
    [hideKeyboard, keyboardVisible]
  );

  // Add a direct event listener to the content area to ensure all taps are captured
  useEffect(() => {
    const contentElement = contentRef.current;

    if (!contentElement) return;

    const handleContentTap = (e: MouseEvent) => {
      if (isTapInProgress.current) return;

      const target = e.target as HTMLElement;
      const isInteractiveElement =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.tagName === 'BUTTON' ||
        target.closest('input') ||
        target.closest('textarea') ||
        target.closest('select') ||
        target.closest('button') ||
        target.closest('[role="button"]');

      if (!isInteractiveElement && keyboardVisible) {
        // Mark that we're processing a tap event
        isTapInProgress.current = true;

        // Hide the keyboard
        hideKeyboard();

        // Reset the flag after a delay
        setTimeout(() => {
          isTapInProgress.current = false;
        }, 300);
      }
    };

    contentElement.addEventListener('click', handleContentTap);

    return () => {
      contentElement.removeEventListener('click', handleContentTap);
    };
  }, [contentRef, hideKeyboard, keyboardVisible]);

  // Function to enhance children with the ability to register their input refs
  const enhanceChildrenWithRegisterInput = (
    childrenToEnhance: React.ReactNode
  ): React.ReactNode => {
    return React.Children.map(childrenToEnhance, (child) => {
      // Only process React elements
      if (!React.isValidElement(child)) return child;

      // Clone the element with additional props
      const enhancedChild = React.cloneElement(
        child as React.ReactElement<any>,
        {
          registerInput,
        }
      );

      // If this child has children, recursively enhance them too
      if (child.props && child.props.children) {
        return React.cloneElement(
          enhancedChild,
          {},
          enhanceChildrenWithRegisterInput(child.props.children)
        );
      }

      return enhancedChild;
    });
  };

  // Calculate dynamic padding for content
  const contentPaddingBottom = isAndroid
    ? '96px' // Match footer spacer height for Android (80px padding + 16px buffer)
    : keyboardVisible
    ? '80px' // Match footer spacer height when keyboard is visible (64px padding + 16px buffer)
    : '104px'; // Match footer spacer height when keyboard is hidden (88px padding + 16px buffer)

  // Calculate dynamic max-height for content to ensure it can shrink when keyboard appears
  const calculateContentMaxHeight = () => {
    if (keyboardVisible && keyboardHeight > 0) {
      // When keyboard is visible, limit the content height to create overflow
      const viewportHeight = window.innerHeight;
      const footerHeight = isAndroid ? 96 : 88; // Footer height including padding
      const headerHeight = 116; // Approximate header height from the screenshot

      if (isAndroid) {
        // Android: WebView shrinks, but we still need to account for our footer
        return `calc(100vh - ${footerHeight}px)`;
      } else {
        // iOS: Subtract keyboard height and footer to create proper overflow
        const maxHeight =
          viewportHeight - keyboardHeight - footerHeight - headerHeight;
        return `${Math.max(200, maxHeight)}px`;
      }
    }

    // When keyboard is hidden, allow content to use available space
    return 'none';
  };

  const contentMaxHeight = calculateContentMaxHeight();

  // Calculate footer padding based on platform and keyboard state
  const calculateFooterPadding = () => {
    if (isAndroid) return 16;
    if (keyboardVisible) return 16;
    return 'calc(88px + env(safe-area-inset-bottom, 0px))';
  };

  // Log footer positioning and content padding changes for debugging
  useEffect(() => {
    const bottomValue = keyboardVisible ? `${keyboardHeight}px` : '0';

    // Platform-specific footer padding calculation
    let footerPaddingValue;
    if (isAndroid) {
      // Android: WebView always resizes, so footer stays at bottom (keyboardHeight = 0)
      // Use consistent padding since WebView shrinking provides the space
      footerPaddingValue = 'calc(80px + env(safe-area-inset-bottom, 0px))';
    } else {
      // iOS: Footer moves up by keyboard height, adjust padding accordingly
      footerPaddingValue = keyboardVisible
        ? 'calc(64px)'
        : 'calc(88px + env(safe-area-inset-bottom, 0px))';
    }

    console.log(
      `[KeyboardAwareLayout] Platform: ${platform}, Footer bottom: ${bottomValue}, Footer padding: ${footerPaddingValue}, Content padding-bottom: ${contentPaddingBottom}, ContentInner max-height: ${contentMaxHeight}, Keyboard visible: ${keyboardVisible}, Keyboard height: ${keyboardHeight}px`
    );
  }, [
    keyboardVisible,
    keyboardHeight,
    platform,
    isAndroid,
    contentPaddingBottom,
    contentMaxHeight,
  ]);

  return (
    <IonPage className={styles.KeyboardAwareLayout}>
      {header && (
        <IonHeader className={styles.KeyboardAwareLayout__Header}>
          {header}
        </IonHeader>
      )}

      <IonContent
        className={`${styles.KeyboardAwareLayout__Content} ${contentClassName}`}
        fullscreen
        scrollEvents={true}
        onClick={handleBackgroundTap}
        {...contentProps}
        style={{
          // Merge existing styles from contentProps with our dynamic styles
          ...(contentProps.style || {}),
          // Dynamic padding-bottom to ensure content can scroll properly above the footer
          '--padding-bottom': contentPaddingBottom,
          // Override Ionic's offset values to prevent conflicts
          '--offset-bottom': '0px',
        }}
      >
        <div
          className={styles.KeyboardAwareLayout__ContentInner}
          ref={contentRef as any}
          style={{
            // Apply height constraint to inner content instead of IonContent
            // This prevents the container from shifting position
            maxHeight: contentMaxHeight,
            overflowY: contentMaxHeight !== 'none' ? 'auto' : 'visible',
            // Add padding at bottom to ensure complete scrolling - more generous when keyboard is visible
            paddingBottom:
              contentMaxHeight !== 'none'
                ? keyboardVisible
                  ? '80px'
                  : '40px'
                : '16px',
          }}
        >
          {enhanceChildrenWithRegisterInput(children)}
        </div>

        {/* Add a spacer at the bottom to ensure content isn't hidden under the footer */}
        {footer && (
          <div
            className={styles.KeyboardAwareLayout__FooterSpacer}
            style={{
              height: contentPaddingBottom, // Use the same height as content padding for consistency
            }}
          />
        )}
      </IonContent>

      {/* Fixed position footer */}
      {footer && (
        <div
          className={styles.FixedFooter}
          style={{
            bottom: keyboardVisible ? `${keyboardHeight}px` : '0',
            paddingBottom: calculateFooterPadding(),
          }}
        >
          {footer}
        </div>
      )}
    </IonPage>
  );
};

export default KeyboardAwareLayout;
